// Reading pipelines on the client.  tickets[9304]
//
// The database owns the shape (0058) and the defaults a new ticket picks up.
// These are the presentation rules on top: which pipelines a department may
// offer, which steps a department's status allow-list would hide, and how far
// along a ticket is. Pure, so the rules are pinned by tests rather than by
// whichever component happens to draw them first.

import { ancestorIds, flattenTree, type TreeNode } from "@/lib/pageTree";

export interface Pipeline {
  id: string;
  name: string;
  description: string | null;
  order_index: number;
  /** Step statuses in order: the first is where a new ticket starts. */
  statusIds: string[];
  /**
   * Departments that chose it (pipeline_subsheets, department-side since 0060).
   * Not "where it may be used" on its own: see allowedPipelineIds.
   */
  subsheetIds: string[];
}

/** A pipelines row with its steps and department limits embedded. */
export interface PipelineRow {
  id: string;
  name: string;
  description: string | null;
  order_index: number;
  pipeline_steps?: Array<{ status_id: string; position: number }> | null;
  pipeline_subsheets?: Array<{ subsheet_id: string }> | null;
}

export function toPipeline(row: PipelineRow): Pipeline {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    order_index: row.order_index,
    statusIds: [...(row.pipeline_steps ?? [])].sort((a, b) => a.position - b.position).map((s) => s.status_id),
    subsheetIds: (row.pipeline_subsheets ?? []).map((s) => s.subsheet_id),
  };
}

/**
 * The pipelines a department offers, read from the department's side (0060):
 * its own choice, else its nearest parent's, else null — every pipeline. A
 * pipeline's `subsheetIds` are the departments that chose it.
 */
export function allowedPipelineIds(
  pipelines: readonly Pipeline[],
  subsheetId: string,
  pages: readonly TreeNode[],
): Set<string> | null {
  const chosenBy = new Map<string, Set<string>>();
  for (const p of pipelines) {
    for (const dept of p.subsheetIds) {
      const set = chosenBy.get(dept) ?? new Set<string>();
      set.add(p.id);
      chosenBy.set(dept, set);
    }
  }
  const own = chosenBy.get(subsheetId);
  if (own) return own;
  // ancestorIds keeps nearest-first order, so the first match is the nearest.
  for (const id of ancestorIds(pages, subsheetId)) {
    const inherited = chosenBy.get(id);
    if (inherited) return inherited;
  }
  return null;
}

/** Whether `pipeline` may be offered in `subsheetId` (decision 3, department-side since 0060). */
export function pipelineAllowedIn(
  pipeline: Pipeline,
  subsheetId: string,
  pages: readonly TreeNode[],
  pipelines: readonly Pipeline[],
): boolean {
  const allowed = allowedPipelineIds(pipelines, subsheetId, pages);
  return allowed === null || allowed.has(pipeline.id);
}

/** The departments offering `pipeline`, in tree order, or "all" when every department does. */
export function departmentsUsing(
  pipeline: Pipeline,
  pipelines: readonly Pipeline[],
  pages: readonly TreeNode[],
): string[] | "all" {
  const ids = flattenTree(pages)
    .map(({ page }) => page.id)
    .filter((id) => pipelineAllowedIn(pipeline, id, pages, pipelines));
  return ids.length === pages.length ? "all" : ids;
}

/** subsheet_statuses rows grouped into each department's allow-list. A department with no rows offers every status. */
export function statusesByDepartment(
  rows: ReadonlyArray<{ subsheet_id: string; status_id: string }>,
): Map<string, Set<string>> {
  const byDepartment = new Map<string, Set<string>>();
  for (const row of rows) {
    const set = byDepartment.get(row.subsheet_id) ?? new Set<string>();
    set.add(row.status_id);
    byDepartment.set(row.subsheet_id, set);
  }
  return byDepartment;
}

interface HiddenSteps {
  subsheetId: string;
  /** Pipeline steps this department's status allow-list does not offer, in step order. */
  statusIds: string[];
}

/**
 * Departments that offer the pipeline but cannot pick some of its steps,
 * because they limit their statuses (subsheet_statuses) and leave those out.
 * Allowed, but worth a warning in the editor (decision 8). A department with
 * no status allow-list offers every status and never appears.
 */
export function hiddenSteps(
  pipeline: Pipeline,
  subsheetStatuses: ReadonlyArray<{ subsheet_id: string; status_id: string }>,
  pages: readonly TreeNode[],
  pipelines: readonly Pipeline[],
): HiddenSteps[] {
  const offered = statusesByDepartment(subsheetStatuses);

  const out: HiddenSteps[] = [];
  for (const page of pages) {
    const allowList = offered.get(page.id);
    if (!allowList || !pipelineAllowedIn(pipeline, page.id, pages, pipelines)) continue;
    const missing = pipeline.statusIds.filter((id) => !allowList.has(id));
    if (missing.length > 0) out.push({ subsheetId: page.id, statusIds: missing });
  }
  return out;
}

export interface PipelineProgress {
  /** Zero-based step the ticket is on. */
  index: number;
  total: number;
  /** 0 on the first step, 100 on the last, rounded. A one-step pipeline is 100. */
  percent: number;
}

/** How far along a ticket on `statusId` is, or null when its status is not a step. */
export function pipelineProgress(stepStatusIds: readonly string[], statusId: string | null): PipelineProgress | null {
  if (!statusId) return null;
  const index = stepStatusIds.indexOf(statusId);
  if (index === -1) return null;
  const total = stepStatusIds.length;
  const percent = total === 1 ? 100 : Math.round((index / (total - 1)) * 100);
  return { index, total, percent };
}

/**
 * The pipeline a new ticket filed in `subsheetId` starts on: the department's
 * own default, else the nearest parent's (decision 2). The client's copy of
 * the rule backlog_items_pipeline() applies at insert, used to pre-select the
 * starting status in the form so the person sees it before saving.
 */
export function effectiveDefaultPipelineId<T extends TreeNode & { default_pipeline_id: string | null }>(
  pages: readonly T[],
  subsheetId: string,
): string | null {
  const byId = new Map(pages.map((p) => [p.id, p]));
  for (const id of [subsheetId, ...ancestorIds(pages, subsheetId)]) {
    const page = byId.get(id);
    if (page?.default_pipeline_id) return page.default_pipeline_id;
  }
  return null;
}

/**
 * A status list with the pipeline's steps first, in step order, and the other
 * statuses after them as they were — the status picker on a ticket that has a
 * pipeline. Steps the list does not offer (a department allow-list) are left
 * out rather than added back.
 */
export function orderByPipeline<T extends { id: string }>(
  options: readonly T[],
  stepStatusIds: readonly string[],
): T[] {
  if (stepStatusIds.length === 0) return [...options];
  const byId = new Map(options.map((o) => [o.id, o]));
  const steps = stepStatusIds.flatMap((id) => byId.get(id) ?? []);
  const onPipeline = new Set(stepStatusIds);
  return [...steps, ...options.filter((o) => !onPipeline.has(o.id))];
}

/**
 * The status "Move to next step" sets: the step after the current one, or the
 * first step when the ticket is off the pipeline (or has no status). Null on
 * the last step and for a pipeline with no steps.
 */
export function nextStepStatusId(stepStatusIds: readonly string[], statusId: string | null): string | null {
  if (stepStatusIds.length === 0) return null;
  const index = statusId ? stepStatusIds.indexOf(statusId) : -1;
  if (index === -1) return stepStatusIds[0];
  return stepStatusIds[index + 1] ?? null;
}

/** "No pipeline" as a filter or Select value (Select needs a non-empty one). */
export const NO_PIPELINE = "__none__";

/** Whether a ticket on `pipelineId` passes the list's Pipeline filter. Empty selection passes everything. */
export function matchesPipelineFilter(pipelineId: string | null, selected: readonly string[]): boolean {
  if (selected.length === 0) return true;
  return selected.includes(pipelineId ?? NO_PIPELINE);
}

/**
 * What the Manage pipelines dialog saves for a department
 * (set_subsheet_pipelines): no rows when it allows everything at the top or
 * follows its parent when nested — the "absence means same as above" rule —
 * else exactly the checked pipelines. A nested department that checks every
 * pipeline still saves them all, because no rows would mean its parent's
 * narrower list. A default outside its own list is dropped, as the database
 * would refuse it.
 */
export function departmentPipelinesPayload({
  nested,
  sameAsParent,
  checked,
  allIds,
  defaultId,
}: {
  nested: boolean;
  sameAsParent: boolean;
  checked: readonly string[];
  allIds: readonly string[];
  defaultId: string | null;
}): { pipelineIds: string[]; defaultId: string | null } {
  const followsAbove = nested ? sameAsParent : allIds.every((id) => checked.includes(id));
  if (followsAbove) return { pipelineIds: [], defaultId };
  const pipelineIds = allIds.filter((id) => checked.includes(id));
  return { pipelineIds, defaultId: defaultId && pipelineIds.includes(defaultId) ? defaultId : null };
}
